import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Box,
  Typography,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  ExitToApp as LogoutIcon,
  Help as HelpIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from '@mui/icons-material';
import NotificationBell from '../NotificationBell';
import EnablePushButton from '../EnablePushButton';
import { useAppTheme } from '../../context/ThemeContext';
import './Topbar.css';

interface TopbarProps {
  onToggleSidebar: () => void;
  sidebarOpen: boolean;
}

const Topbar: React.FC<TopbarProps> = ({ onToggleSidebar, sidebarOpen }) => {
  const navigate = useNavigate();
  const theme = useMuiTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, logout } = useAuth();

  const { theme: themeMode, toggleTheme } = useAppTheme();
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const getUserInitials = (name?: string) => {
    return name
      ? name.split(' ').map((part) => part.charAt(0)).join('').toUpperCase()
      : 'U';
  };

  return (
    <header className="topbar-header">
      <div className="topbar-inner">
        {/* Mobile menu button */}
        {!isDesktop && (
          <div className="topbar-menu-btn-wrap">
            <button
              type="button"
              className="topbar-menu-btn"
              onClick={onToggleSidebar}
            >
              <MenuIcon sx={{ fontSize: 24 }} />
            </button>
          </div>
        )}

        {/* Search */}
        <div className="topbar-search-wrap">
          <div className="topbar-search-box">
            <div className="topbar-search-icon">
              <SearchIcon sx={{ fontSize: 20, color: '#9ca3af' }} />
            </div>
            <input
              type="search"
              placeholder={isMobile ? 'جستجو...' : 'جستجو در محصولات، سفارشات و ...'}
              className="topbar-search-input"
              dir="rtl"
            />
          </div>
        </div>

        {/* Right side */}
        <div className="topbar-actions">
          {/* Theme Toggle */}
          <IconButton onClick={toggleTheme} sx={{ color: '#6b7280' }}>
            {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>

          {/* Push opt-in (hidden once permission is granted/blocked) */}
          <EnablePushButton />

          {/* Notifications */}
          <NotificationBell />

          {/* User info (desktop only) */}
          {isDesktop && user?.role && (
            <span className="topbar-user-role">
              {user.role}
            </span>
          )}

          {/* Profile avatar */}
          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{ p: 0.5 }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: '#B25373',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              {getUserInitials(user?.name)}
            </Avatar>
          </IconButton>
        </div>
      </div>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 180,
            borderRadius: 2,
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
            border: '1px solid #e5e7eb',
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        <Box sx={{ py: 1, px: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
            {user?.name || 'کاربر مهمان'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
            {user?.email || ''}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => { handleMenuClose(); navigate('/profile'); }}
          sx={{ borderRadius: 1, mx: 0.5, fontSize: 14 }}
        >
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          <Typography variant="body2">پروفایل</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => { handleMenuClose(); navigate('/settings'); }}
          sx={{ borderRadius: 1, mx: 0.5, fontSize: 14 }}
        >
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          <Typography variant="body2">تنظیمات</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => { handleMenuClose(); navigate('/help'); }}
          sx={{ borderRadius: 1, mx: 0.5, fontSize: 14 }}
        >
          <ListItemIcon><HelpIcon fontSize="small" /></ListItemIcon>
          <Typography variant="body2">راهنما</Typography>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleLogout}
          sx={{ borderRadius: 1, mx: 0.5, fontSize: 14 }}
        >
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <Typography variant="body2">خروج</Typography>
        </MenuItem>
      </Menu>
    </header>
  );
};

export default Topbar;
